import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GiftIcon } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../ui/dialog";
import SpinnerSmall from "../SpinnerSmall";
import { useActivateTrial } from "@/tanstack-hooks";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import tada from "@/assets/tada.png";

/**
 * Sentinel used as the "selected plan" value when the user picks the free
 * trial. It can't collide with a real plan/package id (those are UUIDs /
 * StoreKit identifiers), so both the web and iOS selectors reuse their
 * existing selection state to track the trial.
 */
export const TRIAL_PLAN_ID = "__trial__";

/**
 * Self-contained "Activate" CTA shown in place of the Pay button when the
 * free trial is selected. Owns its confirmation modal, the one-time
 * activation mutation, and the success toast/navigation, so the web (Stripe)
 * and iOS (RevenueCat) selectors stay identical and don't carry any trial
 * dialog state themselves. The trial is driven entirely by `trial_eligible`
 * and the dedicated activate-trial endpoint — it never touches the
 * plan-listing endpoint.
 */
export default function ActivateTrialButton() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const activateTrialMutation = useActivateTrial();
  const { toast } = useToast();
  const t = useTranslation();

  function handleActivate() {
    activateTrialMutation.mutate(undefined, {
      onSuccess: () => {
        setOpen(false);
        toast({
          variant: "default",
          // @ts-expect-error Type 'Element' is not assignable to type
          title: (
            <div className="flex items-center gap-2">
              <img src={tada} className="w-5 h-5" />
              <span>{t.SELECT_PLAN_CARD.LABELS.TRIAL_ACTIVATED_SUCCESS}</span>
            </div>
          ),
          className: "dark rounded-2xl",
        });
        navigate("/settings");
      },
      onError: (error) => {
        console.error("Could not activate trial", error);
        setOpen(false);
        toast({
          variant: "destructive",
          title: t.SELECT_PLAN_CARD.LABELS.TRIAL_ACTIVATION_FAILED,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark rounded-2xl",
        });
      },
    });
  }

  return (
    <>
      <Button
        className="w-full rounded-full py-6"
        onClick={() => setOpen(true)}
      >
        <GiftIcon className="w-5 h-5 mr-2" />
        {t.SELECT_PLAN_CARD.LABELS.ACTIVATE}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="dark">
          <DialogTitle className="text-white flex flex-row items-center">
            {t.SELECT_PLAN_CARD.LABELS.TRIAL_DIALOG_TITLE}
          </DialogTitle>
          <DialogDescription>
            {t.SELECT_PLAN_CARD.LABELS.TRIAL_DIALOG_DESCRIPTION}
          </DialogDescription>
          <DialogFooter>
            <div className="w-full flex flex-row justify-between">
              <Button
                onClick={handleActivate}
                disabled={activateTrialMutation.isPending}
              >
                {activateTrialMutation.isPending ? (
                  <SpinnerSmall black={true} />
                ) : (
                  <>{t.SELECT_PLAN_CARD.LABELS.ACTIVATE}</>
                )}
              </Button>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                {t.SHARED.LABELS.BACK_BUTTON}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
